import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Bold, Italic, Heading1, Heading2, List, ListOrdered, Link2, Undo, Redo, Eye, Code, Loader2, Check,
  Image as ImageIcon, Video, AlignLeft, AlignCenter, AlignRight, AlignJustify,
} from "lucide-react";
import { ImageLibraryModal } from "./ImageLibraryModal";
import { getVideoEmbedSrc } from "./tiptapShapes";

// Custom hook for debouncing
function useDebounce(callback, delay) {
  const timeoutRef = useRef(null);
  
  return useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
}

export function WysiwygEditor({ value, onSave, placeholder = "Start typing..." }) {
  const editorRef = useRef(null);
  const wrapperRef = useRef(null);
  const [showSource, setShowSource] = useState(false);
  const [sourceContent, setSourceContent] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const initialLoadRef = useRef(true);
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  const [mediaToolbar, setMediaToolbar] = useState(null); // { el, type, top, left }
  const resizeObserverRef = useRef(null);

  // Set initial content
  useEffect(() => {
    if (editorRef.current && value && initialLoadRef.current) {
      editorRef.current.innerHTML = value;
      setSourceContent(value);
      initialLoadRef.current = false;
    }
  }, [value]);

  // Auto-save function
  const performAutoSave = useCallback(async (content) => {
    if (!content || saving) return;
    
    setSaving(true);
    try {
      await onSave(content);
      setLastSaved(new Date());
      setHasChanges(false);
    } catch (error) {
      console.error("Auto-save error:", error);
    } finally {
      setSaving(false);
    }
  }, [onSave, saving]);

  // Debounced auto-save (2 second delay)
  const debouncedSave = useDebounce(performAutoSave, 2000);

  // Handle content changes
  const handleInput = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      setSourceContent(html);
      setHasChanges(true);
      debouncedSave(html);
    }
  };

  // Handle source code changes
  const handleSourceChange = (e) => {
    const html = e.target.value;
    setSourceContent(html);
    setHasChanges(true);
    debouncedSave(html);
  };

  // Sync source to editor when switching views
  const toggleSourceView = () => {
    if (showSource && editorRef.current) {
      editorRef.current.innerHTML = sourceContent;
    }
    setShowSource(!showSource);
  };

  // Execute formatting command
  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  // Insert HTML at cursor
  const insertHTML = (html) => {
    document.execCommand('insertHTML', false, html);
    editorRef.current?.focus();
    handleInput();
  };

  // Insert an image — resizable by dragging its bottom-right corner (native
  // CSS `resize`, see .wys-content img below) or via the mini toolbar that
  // appears when it's clicked (see handleEditorClick).
  const insertImage = (url) => {
    insertHTML(
      `<img src="${url}" alt="" class="wys-img" style="width:100%;max-width:100%;display:block;margin:1rem 0;object-fit:cover;border-radius:8px;" />`
    );
  };

  // Insert a video embed — reuses the same `.video-embed` markup/CSS as the
  // Tiptap editor and the public site's RichHtml renderer, so all three
  // look identical and share the float/resize rules in App.css.
  const insertVideo = (url) => {
    const src = getVideoEmbedSrc(url);
    if (!src) return;
    insertHTML(
      `<div class="video-embed" data-video-src="${src}" style="width:100%;"><div class="video-embed-inner"><iframe src="${src}" frameborder="0" loading="lazy" allowfullscreen></iframe></div></div><p><br></p>`
    );
  };

  const addVideo = () => {
    const url = prompt('Video URL (YouTube, Vimeo, or direct link):');
    if (url) insertVideo(url);
  };

  // Commit a drag-resized element's live pixel width back to a % width,
  // and re-run handleInput so the change is saved. Same approach as the
  // Tiptap editor's ResizeObserver-based commit.
  const commitDraggedWidth = useCallback((el) => {
    if (!editorRef.current) return;
    const containerWidth = editorRef.current.getBoundingClientRect().width;
    if (!containerWidth) return;
    const pct = Math.max(10, Math.min(100, Math.round((el.getBoundingClientRect().width / containerWidth) * 100)));
    el.style.width = `${pct}%`;
    handleInput();
  }, []);

  useEffect(() => {
    // Skip each element's spec-mandated initial callback (fires the
    // instant .observe() starts, even with no real resize yet) — same
    // fix as TiptapEditor's ResizeObserver, see its comment for details.
    const seenOnce = new WeakSet();
    resizeObserverRef.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (!seenOnce.has(entry.target)) {
          seenOnce.add(entry.target);
          continue;
        }
        commitDraggedWidth(entry.target);
      }
    });
    return () => resizeObserverRef.current?.disconnect();
  }, [commitDraggedWidth]);

  // Clicking an inserted image/video shows a small floating toolbar (width
  // presets + float left/right/none) positioned next to it, and starts
  // watching it for native corner-drag resizes.
  const handleEditorClick = (e) => {
    const mediaEl = e.target.tagName === 'IMG' ? e.target : e.target.closest?.('.video-embed');
    if (!mediaEl || !wrapperRef.current) {
      setMediaToolbar(null);
      return;
    }
    resizeObserverRef.current?.observe(mediaEl);
    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const elRect = mediaEl.getBoundingClientRect();
    setMediaToolbar({
      el: mediaEl,
      type: mediaEl.tagName === 'IMG' ? 'image' : 'video',
      top: elRect.top - wrapperRect.top + wrapperRef.current.scrollTop,
      left: elRect.left - wrapperRect.left,
    });
  };

  const setMediaWidth = (pct) => {
    if (!mediaToolbar?.el) return;
    mediaToolbar.el.style.width = `${pct}%`;
    handleInput();
  };

  const setMediaFloat = (float) => {
    if (!mediaToolbar?.el) return;
    const el = mediaToolbar.el;
    if (float === 'none') {
      el.style.float = 'none';
      el.style.margin = mediaToolbar.type === 'image' ? '1rem 0' : '1.5rem 0';
    } else {
      el.style.float = float;
      el.style.margin = float === 'left' ? '0.25rem 1.5rem 1rem 0' : '0.25rem 0 1rem 1.5rem';
    }
    handleInput();
  };

  const toolbarButtons = [
    { icon: Heading1, label: "Heading 2", action: () => execCommand('formatBlock', 'h2') },
    { icon: Heading2, label: "Heading 3", action: () => execCommand('formatBlock', 'h3') },
    { divider: true },
    { icon: Bold, label: "Bold", action: () => execCommand('bold') },
    { icon: Italic, label: "Italic", action: () => execCommand('italic') },
    { divider: true },
    { icon: List, label: "Bullet List", action: () => execCommand('insertUnorderedList') },
    { icon: ListOrdered, label: "Numbered List", action: () => execCommand('insertOrderedList') },
    { divider: true },
    { icon: AlignLeft, label: "Align left", action: () => execCommand('justifyLeft') },
    { icon: AlignCenter, label: "Align centre", action: () => execCommand('justifyCenter') },
    { icon: AlignRight, label: "Align right", action: () => execCommand('justifyRight') },
    { icon: AlignJustify, label: "Justify", action: () => execCommand('justifyFull') },
    { divider: true },
    { icon: ImageIcon, label: "Insert Image", action: () => setShowImageLibrary(true), testId: "wys-insert-image-btn" },
    { icon: Video, label: "Insert Video", action: addVideo, testId: "wys-insert-video-btn" },
    { icon: Link2, label: "Insert Link", action: () => {
      const url = prompt('Enter URL:');
      if (url) execCommand('createLink', url);
    }},
    { divider: true },
    { icon: Undo, label: "Undo", action: () => execCommand('undo') },
    { icon: Redo, label: "Redo", action: () => execCommand('redo') },
  ];

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 bg-muted/30 border-b border-border flex-wrap">
        {toolbarButtons.map((btn, index) => 
          btn.divider ? (
            <div key={index} className="w-px h-6 bg-border mx-1" />
          ) : (
            <button
              key={index}
              type="button"
              onClick={btn.action}
              title={btn.label}
              data-testid={btn.testId}
              className="p-1.5 hover:bg-muted rounded transition-colors"
            >
              <btn.icon size={16} />
            </button>
          )
        )}
        <div className="flex-1" />
        
        {/* Status indicator */}
        <div className="flex items-center gap-2 text-xs mr-2">
          {saving ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
              <span className="text-muted-foreground">Saving...</span>
            </>
          ) : hasChanges ? (
            <span className="text-amber-600">Unsaved</span>
          ) : lastSaved ? (
            <>
              <Check className="h-3 w-3 text-green-600" />
              <span className="text-green-600">Saved</span>
            </>
          ) : null}
        </div>
        
        <button
          type="button"
          onClick={toggleSourceView}
          className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
            showSource ? "bg-primary text-white" : "hover:bg-muted border border-border"
          }`}
        >
          {showSource ? <Eye size={12} /> : <Code size={12} />}
          {showSource ? "Preview" : "HTML"}
        </button>
      </div>
      
      {/* Editor / Source */}
      {showSource ? (
        <textarea
          value={sourceContent}
          onChange={handleSourceChange}
          className="w-full h-[250px] p-4 font-mono text-sm bg-slate-50 resize-none focus:outline-none"
          placeholder="Enter HTML..."
          rows={10}
        />
      ) : (
        <div ref={wrapperRef} className="relative">
          <div
            ref={editorRef}
            contentEditable
            onInput={handleInput}
            onClick={handleEditorClick}
            className="wys-content h-[250px] overflow-y-auto p-4 focus:outline-none prose prose-slate max-w-none
              prose-headings:font-serif prose-headings:text-foreground
              prose-h2:text-xl prose-h2:mt-4 prose-h2:mb-2 prose-h2:border-b prose-h2:pb-2 prose-h2:border-border
              prose-h3:text-lg prose-h3:mt-3 prose-h3:mb-2
              prose-p:my-2 prose-p:text-muted-foreground
              prose-li:text-muted-foreground prose-li:my-0.5
              prose-ul:my-2 prose-ol:my-2
              prose-a:text-primary prose-a:no-underline hover:prose-a:underline
              [&:empty]:before:content-[attr(data-placeholder)] [&:empty]:before:text-muted-foreground/50
            "
            data-placeholder={placeholder}
            suppressContentEditableWarning
          />

          {/* Mini toolbar for the clicked image/video: width presets + float */}
          {mediaToolbar && (
            <div
              className="absolute z-20 flex items-center gap-1 bg-white border border-border rounded shadow-lg p-1"
              style={{ top: Math.max(0, mediaToolbar.top - 40), left: mediaToolbar.left }}
              data-testid="wys-media-toolbar"
            >
              {["25", "50", "75", "100"].map((w) => (
                <button
                  key={w}
                  type="button"
                  onClick={() => setMediaWidth(w)}
                  className="px-1.5 py-1 text-xs hover:bg-muted rounded"
                  data-testid={`wys-media-width-${w}`}
                >
                  {w}%
                </button>
              ))}
              <div className="w-px h-5 bg-border mx-0.5" />
              <button type="button" title="No wrap" onClick={() => setMediaFloat('none')} className="p-1 hover:bg-muted rounded" data-testid="wys-media-float-none">
                <AlignJustify size={14} />
              </button>
              <button type="button" title="Float left" onClick={() => setMediaFloat('left')} className="p-1 hover:bg-muted rounded" data-testid="wys-media-float-left">
                <AlignLeft size={14} />
              </button>
              <button type="button" title="Float right" onClick={() => setMediaFloat('right')} className="p-1 hover:bg-muted rounded" data-testid="wys-media-float-right">
                <AlignRight size={14} />
              </button>
            </div>
          )}

          <style>{`
            /* Drag-resize handle on inserted images/videos — width-only for
               videos to preserve their 16:9 box, both directions for images. */
            .wys-content img.wys-img {
              cursor: pointer;
              resize: both;
              overflow: hidden;
            }
            .wys-content .video-embed {
              cursor: pointer;
              resize: horizontal;
              overflow: hidden;
            }
          `}</style>
        </div>
      )}

      <ImageLibraryModal
        open={showImageLibrary}
        onClose={() => setShowImageLibrary(false)}
        onSelect={(url, opts = {}) => {
          if (opts.isVideo) insertVideo(url);
          else insertImage(url);
          setShowImageLibrary(false);
        }}
        allowEdit={false}
      />
    </div>
  );
}

export default WysiwygEditor;
